import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import {useCallback, useEffect, useRef, useState} from 'react'
import type {Finding, FindingsPayload, Freshness, MergeReadiness} from './types'

interface UseFindingsOptions {
  pullRequestBasePath: string
}

interface UseFindingsState {
  findings: Finding[]
  ignoredEntityIds: string[]
  freshness: Freshness
  mergeReadiness: MergeReadiness | null
  overviewSummary: string | null
  modelsUrl: string | null
  isLoading: boolean
  error: string | null
}

const INITIAL_STATE: UseFindingsState = {
  findings: [],
  ignoredEntityIds: [],
  freshness: 'pending',
  mergeReadiness: null,
  overviewSummary: null,
  modelsUrl: null,
  isLoading: true,
  error: null,
}

const DEFAULT_RETRY_AFTER_SECONDS = 10
const MIN_RETRY_AFTER_SECONDS = 1

const getRetryAfterMs = (retryAfterHeader: string | null): number => {
  const parsedRetryAfter = retryAfterHeader === null ? Number.NaN : Number(retryAfterHeader)
  const retryAfterSeconds = Number.isFinite(parsedRetryAfter)
    ? Math.max(MIN_RETRY_AFTER_SECONDS, parsedRetryAfter)
    : DEFAULT_RETRY_AFTER_SECONDS

  return retryAfterSeconds * 1000
}

export function useFindings({pullRequestBasePath}: UseFindingsOptions): UseFindingsState {
  const [state, setState] = useState<UseFindingsState>(INITIAL_STATE)
  const pollingRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const mountedRef = useRef(true)

  const fetchFindings = useCallback(async () => {
    try {
      const response = await verifiedFetchJSON(`${pullRequestBasePath}/code_review_findings`)
      if (!response.ok) {
        throw new Error(`Failed to fetch findings (${response.status})`)
      }
      const data: FindingsPayload = await response.json()

      if (!mountedRef.current) return

      setState({
        findings: data.findings,
        ignoredEntityIds: data.ignoredEntityIds,
        freshness: data.freshness,
        mergeReadiness: data.mergeReadiness,
        overviewSummary: data.overviewSummary,
        modelsUrl: data.modelsUrl ?? null,
        isLoading: false,
        error: null,
      })

      if (data.freshness !== 'current') {
        pollingRef.current = setTimeout(
          () => {
            if (mountedRef.current) {
              void fetchFindings()
            }
          },
          getRetryAfterMs(response.headers.get('Retry-After')),
        )
      }
    } catch (e) {
      if (!mountedRef.current) return
      setState(prev => ({
        ...prev,
        error: e instanceof Error ? e.message : 'Failed to fetch findings',
        isLoading: false,
      }))
    }
  }, [pullRequestBasePath])

  useEffect(() => {
    mountedRef.current = true
    void fetchFindings()

    return () => {
      mountedRef.current = false
      if (pollingRef.current) {
        clearTimeout(pollingRef.current)
      }
    }
  }, [fetchFindings])

  return state
}
